import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AdminBarComponent } from '../layout/admin-bar/admin-bar.component';
import { TermsEditorComponent } from './components/terms-editor/terms-editor.component';
import { AccessibilityEditorComponent } from './components/accessibility-editor/accessibility-editor.component';
import { SitesTableComponent } from './sites-table/sites-table.component';
import { BrandingEditorComponent } from './components/branding-editor/branding-editor.component';
import { EmailEditorComponent } from './components/email-editor/email-editor.component';
import { IntegrationEditorComponent } from './components/integration-editor/integration-editor.component';

@Component({
  selector: 'app-admin-settings',
  templateUrl: './admin-settings.component.html',
  styleUrls: ['./admin-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AdminBarComponent,
    TermsEditorComponent,
    AccessibilityEditorComponent,
    SitesTableComponent,
    BrandingEditorComponent,
    EmailEditorComponent,
    IntegrationEditorComponent
  ]
})
export class AdminSettingsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly title = inject(Title);

  public readonly activeTab = signal('overview');

  constructor() {
    this.title.setTitle('Portal - Settings');


    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params?.['tab']) {
          this.activeTab.set(params['tab']);
        }
      });
  }

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
    
    // Update URL with the selected tab
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
  }

  onKeyDown(event: KeyboardEvent, tab: string): void {
    // Handle Enter and Space key presses for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setActiveTab(tab);
    }
    
    // Handle arrow key navigation
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateWithArrowKeys(event.key);
    }
  }

  private navigateWithArrowKeys(key: string): void {
    const tabs = ['overview', 'branding', 'emails', 'integrations', 'sites'];
    const currentIndex = tabs.indexOf(this.activeTab());
    
    let newIndex: number;
    if (key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }
    
    this.setActiveTab(tabs[newIndex]);
  }
}
